import {
  cellRenderer,
  headerCellRenderer,
  stripHtml,
  toTitleCase,
} from "@utils/helpers";
import Table from "@common/Table";
import { useEffect, useRef, useState } from "react";
import { Button, DatePicker, Empty, Spin, Tooltip } from "antd";
import {
  CalendarOutlined,
  LeftOutlined,
  Loading3QuartersOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { useFetchTableDataQuery, useLazyFetchHTMLQuery } from "@store/products";
import {
  arraysToObjects,
  downloadBlobAsFile,
  objectsToArrays,
} from "@store/utilities";
import { useFetchCalenderMonthsQuery } from "@store/master";
import dayjs from "dayjs";
import {
  BRAZIL_EXPORTS,
  BRAZIL_IMPORTS,
  FDF_US,
  FDF_WORLDWIDE,
  GLOBAL_CONSULTATION,
  INDIAN_EXPORTS,
  INDIAN_IMPORTS,
  MEXICAN_EXPORTS,
  MEXICAN_IMPORTS,
  REGULATORY_FILLING,
} from "../data/activeTabs";
import html2pdf from "html2pdf.js";
import { IconButton, PrimaryButton } from "@common/Buttons";
import downloadActiveIcon from "@assets/icons/download_active.svg";
import downloadIcon from "@assets/icons/download.svg";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { countBy } from "lodash";
import { useDownloadFileMutation } from "@store/file";

const A4_WIDTH = 595.28;
const A4_HEIGHT = 841.89;
const DATA_CREATION_DATE = process.env.REACT_APP_DATA_CREATION_DATE;

const GRAY = "!text-gray-500 !opacity-50";
const RED = "!text-red-500";
const BLACK = "";

const COLORS = {
  black: BLACK,
  gray: GRAY,
  red: RED,
};

const US_DMF_COLOR = "usdmfColor";
const CEP_COLOR = "cepColor";
const WC_COLOR = "wcColor";
const JDMF_COLOR = "jdmfColor";
const KDMF_COLOR = "kdmfColor";
const CDMF_COLOR = "cdmfColor";

const ROW_TYPE = {
  main: "main",
  first: "first",
  second: "second",
  third: "third",
  lastRow: "lastRow",
  none: "none",
};

const generateAntdColumns = (firstRow = [], secondRow = []) => {
  const columns = [];

  // Build contiguous groups: [{ title, size, startIndex }]
  const groups = [];
  for (let i = 0; i < firstRow.length; ) {
    const title = firstRow[i];
    let size = 1;
    while (i + size < firstRow.length && firstRow[i + size] === title) size++;
    groups.push({ title, size, startIndex: i });
    i += size;
  }

  // Detect repeated groups
  const groupsWithRepeat = groups.filter((g) => g.size > 1).length;

  let secondRowIndex = 0;

  // ✅ Case A: repeated groups exist → use normal logic
  if (groupsWithRepeat > 0) {
    for (const g of groups) {
      if (g.size > 1) {
        const children = [];
        for (let j = 0; j < g.size; j++) {
          const subTitle = secondRow[secondRowIndex] || "";
          children.push({
            title: headerCellRenderer(subTitle),
            dataIndex: `${g.title} ${subTitle}`,
            key: `${g.title} ${subTitle}`,
            align: "center",
            render: (text, record) =>
              cellRenderer(
                text,
                `!py-1 !px-2 ${
                  record.type === ROW_TYPE.main
                    ? "!bg-primary-tint !text-white"
                    : ""
                } ${
                  record.type === ROW_TYPE.lastRow
                    ? "!bg-gray-300 !text-black"
                    : ""
                }`,
              ),
            width: 55,
          });
          secondRowIndex++;
        }

        columns.push({
          title: headerCellRenderer(g.title),
          align: "center",
          children,
        });
      } else {
        columns.push({
          title: headerCellRenderer(g.title, "!flex !justify-center px-2"),
          dataIndex: g.title,
          key: g.title,
          width: ["Manufacturer", "Importing Region"].includes(g.title)
            ? 200
            : "",
          render: (text, record) =>
            cellRenderer(
              text,
              `!py-1 !px-2 ${
                record.type === ROW_TYPE.main
                  ? "!bg-primary-tint !text-white"
                  : ""
              } ${record.type === ROW_TYPE.second ? "!px-6" : ""} ${
                record.type === ROW_TYPE.third ? "!px-8" : ""
              } ${
                record.type === ROW_TYPE.lastRow
                  ? "!bg-gray-300 !text-black"
                  : ""
              }`,
            ),
        });
      }
    }

    return columns;
  }

  // ✅ Case B: no repeated groups
  // - First column standalone
  // - Rightmost N = secondRow.length columns → grouped with secondRow years
  const totalGroups = groups.length;
  const rightmostGroupStart = Math.max(totalGroups - secondRow.length, 1); // at least keep 1st standalone

  for (let gi = 0; gi < totalGroups; gi++) {
    const g = groups[gi];

    // If this group is in the rightmost 'year' zone
    if (gi >= rightmostGroupStart) {
      const subTitle = secondRow[secondRowIndex] || "";
      columns.push({
        title: headerCellRenderer(g.title),
        align: "center",
        children: [
          {
            title: headerCellRenderer(subTitle),
            dataIndex: `${g.title} ${subTitle}`,
            key: `${g.title} ${subTitle}`,
            align: "center",
            render: (text, record) =>
              cellRenderer(
                text,
                `!py-1 !px-2 ${
                  record.type === ROW_TYPE.main
                    ? "!bg-primary-tint !text-white"
                    : ""
                } ${
                  record.type === ROW_TYPE.lastRow
                    ? "!bg-gray-300 !text-black"
                    : ""
                }`,
              ),
            width: 55,
          },
        ],
      });
      secondRowIndex++;
    } else {
      // Normal standalone column (like first one)
      columns.push({
        title: headerCellRenderer(g.title, "!flex !justify-center px-2"),
        dataIndex: g.title,
        key: g.title,
        width: ["Manufacturer", "Importing Region"].includes(g.title)
          ? 200
          : "",
        render: (text, record) =>
          cellRenderer(
            text,
            `!py-1 !px-2 ${
              record.type === ROW_TYPE.main
                ? "!bg-primary-tint !text-white"
                : ""
            } ${record.type === ROW_TYPE.second ? "!px-6" : ""} ${
              record.type === ROW_TYPE.third ? "!px-8" : ""
            } ${
              record.type === ROW_TYPE.lastRow ? "!bg-gray-300 !text-black" : ""
            }`,
          ),
      });
    }
  }

  return columns;
};

function combineHeaders(mainHeaders, subHeaders) {
  const result = [];
  let subIndex = 0;

  // Get unique grouped headers (excluding the first identifier column)
  const groupedHeaders = [
    ...new Set(
      mainHeaders.filter(
        (h) =>
          !h.includes("Country of Origin") &&
          !h.includes("Importing Region") &&
          !h.includes("Exporter / Importer") &&
          !h.includes("Country") &&
          !h.includes("Importing Country- Manufacturer- Consignee") &&
          !h.includes("Country of Purchase - Country of Origin") &&
          !h.includes("Manufacturer") &&
          !h.includes("Consignee"),
      ),
    ),
  ];

  for (let i = 0; i < mainHeaders.length; i++) {
    const header = mainHeaders[i];

    if (
      [
        "Country of Origin",
        "Exporter / Importer",
        "Country",
        "Importing Region",
        "Importing Country- Manufacturer- Consignee",
        "Country of Purchase - Country of Origin",
        "Manufacturer",
        "Consignee",
      ].some((h) => header.includes(h))
    ) {
      result.push(header);
    } else {
      // Determine which group (e.g., Qty in Kg or USD/Kg) this subheader belongs to
      const group = groupedHeaders.find((g, idx) => {
        // Each group takes subHeaders.length / number of groups items
        const groupSize = subHeaders.length / groupedHeaders.length;
        const groupStart = idx * groupSize;
        const groupEnd = groupStart + groupSize;
        return subIndex >= groupStart && subIndex < groupEnd;
      });

      result.push(`${group} ${stripHtml(subHeaders[subIndex])}`);
      subIndex++;
    }
  }

  return result;
}

//strip html from first item of the array
const stripHTMLFirstItem = (array) => {
  return [stripHtml(array[0]), ...array.slice(1)];
};

const generateDataSource = (
  tableData,
  data,
  lastRowData = [],
  tableType,
  activeTab,
) => {
  const headers = combineHeaders(
    tableData.firstHeaderRow,
    tableData.secondHeaderRow,
  );

  let formatedData;

  if (["oriConExpImpTbl1", "impConManConsTbl3"].includes(tableType)) {
    if (activeTab === MEXICAN_EXPORTS) {
      formatedData = data.exporters
        .map((item) => {
          const mappedExporter = [
            arraysToObjects([headers, item.exporter], { type: ROW_TYPE.main }),
          ];
          item.countries.forEach((countryItem) => {
            mappedExporter.push(
              arraysToObjects(
                [headers, stripHTMLFirstItem(countryItem.country)],
                {
                  type: ROW_TYPE.second,
                },
              ),
            );
            countryItem.importers.forEach((importerItem) => {
              mappedExporter.push(
                arraysToObjects([headers, stripHTMLFirstItem(importerItem)], {
                  type: ROW_TYPE.third,
                }),
              );
            });
          });
          return mappedExporter;
        })
        .flat(2);
    } else {
      const commonData = data.countries.map((item) => {
        const mappedCountry = [
          arraysToObjects([headers, item.country], { type: ROW_TYPE.main }),
        ];
        item.exporters.forEach((exporterItem) => {
          mappedCountry.push(
            arraysToObjects(
              [headers, stripHTMLFirstItem(exporterItem.exporter)],
              {
                type: ROW_TYPE.second,
              },
            ),
          );
          exporterItem.importers.forEach((importerItem) => {
            mappedCountry.push(
              arraysToObjects([headers, stripHTMLFirstItem(importerItem)], {
                type: ROW_TYPE.third,
              }),
            );
          });
        });
        return mappedCountry;
      });
      formatedData =
        tableType === "oriConExpImpTbl1"
          ? commonData
              .concat(
                arraysToObjects([headers, lastRowData], {
                  type: ROW_TYPE.lastRow,
                }),
              )
              .flat(2)
          : commonData.flat(2);
    }
  } else if (tableType === "expImpTbl4") {
    formatedData = data.exporters
      .map((item) => {
        const mappedExporter = [
          arraysToObjects([headers, stripHTMLFirstItem(item.exporter)], {
            type: ROW_TYPE.main,
          }),
        ];
        item.importers.forEach((importerItem) => {
          mappedExporter.push(
            arraysToObjects([headers, stripHTMLFirstItem(importerItem)], {
              type: ROW_TYPE.second,
            }),
          );
        });
        return mappedExporter;
      })
      .flat(2);
  } else if (["oriConImpTbl1", "arriContOriContTbl1"].includes(tableType)) {
    formatedData = data.countries
      .map((item) => {
        const mappedCountry = [
          arraysToObjects([headers, item.country], { type: ROW_TYPE.main }),
        ];
        item.importers.forEach((importerItem) => {
          mappedCountry.push(
            arraysToObjects([headers, stripHTMLFirstItem(importerItem)], {
              type: ROW_TYPE.second,
            }),
          );
        });
        return mappedCountry;
      })
      .concat(
        arraysToObjects([headers, lastRowData], {
          type: ROW_TYPE.lastRow,
        }),
      )
      .flat(2);
  } else if (["manufTbl1", "impRegTbl2"].includes(tableType)) {
    formatedData = arraysToObjects([headers, ...data], { type: ROW_TYPE.none })
      .concat(
        arraysToObjects([headers, lastRowData], { type: ROW_TYPE.lastRow }),
      )
      .flat(2);
  }

  return formatedData;
};

const TableWithCustomHeader = ({
  tableData,
  tableType,
  activeTab,
  hiddenSidebar,
}) => {
  let columns = [];
  let dataSource = [];
  // formating the structure for brazil imports table
  if (tableType === "arriContOriContTbl1") {
    const [firstRow, secondRow] = tableData.header;
    //columns
    const firstHeaderRow = [];
    const secondHeaderRow = [];
    firstRow?.cells?.forEach((item) => {
      const itemLength = item.colSpan;
      for (let i = 0; i < itemLength; i++) {
        firstHeaderRow.push(item.value);
      }
    });
    secondRow?.cells?.forEach((item) => {
      const itemLength = item.colSpan;
      for (let i = 0; i < itemLength; i++) {
        secondHeaderRow.push(item.value);
      }
    });
    columns = generateAntdColumns(
      firstHeaderRow,
      secondHeaderRow.map((item) => stripHtml(item)),
    );

    //data source
    const body = tableData.body;
    let dataRows = [];
    let grandTotalRow = [];

    body.at(-1)?.cells?.forEach((item) => {
      const itemLength = item.colSpan;
      for (let i = 0; i < itemLength; i++) {
        grandTotalRow.push(item.value);
      }
    });

    let currentCountry = null;
    body.slice(0, -1).forEach((item) => {
      const isCountry = item.style;
      if (isCountry) {
        const country = item.cells.map((cell) => cell.value);
        dataRows.push({ country, importers: [] });
        currentCountry++;
      } else {
        const importer = item.cells.map((cell) => cell.value);
        dataRows[currentCountry - 1].importers.push(importer);
      }
    });

    dataSource = generateDataSource(
      { firstHeaderRow, secondHeaderRow },
      { countries: dataRows },
      grandTotalRow,
      tableType,
      activeTab,
    );
  } else {
    columns = generateAntdColumns(
      tableData.firstHeaderRow,
      tableData.secondHeaderRow.map((item) => stripHtml(item)),
    );
    dataSource = generateDataSource(
      tableData,
      tableData.dataRowsHtml,
      tableData.grandTotalRowHtml,
      tableType,
      activeTab,
    );
  }

  return (
    <div className="h-full min-w-0 border-2 border-black">
      <Table
        key={`${tableType}-${hiddenSidebar}`}
        columns={columns}
        dataSource={dataSource}
        scrollConfig={{ x: "max-content", y: "70vh" }}
        rowClassName={
          ["impConManConsTbl3" ,"oriConExpImpTbl1", "arriContOriContTbl1"].includes(tableType)
            ? (record, index) => {
                return record.type === ROW_TYPE.second ? "!bg-[#eeeeee]" : null;
              }
            : ["oriConImpTbl1"].includes(tableType)
              ? (record, index) => {
                  return record.type === ROW_TYPE.second
                    ? "!bg-white"
                    : null;
                }
              : null
        }
        components={{
          body: {
            cell: (props) => {
              return (
                <td
                  {...props}
                  className={"!py-0 !px-0 !border-y-border !border-x-black"}
                />
              );
            },
          },
          header: {
            cell: (props) => {
              return (
                <th
                  {...props}
                  className="!bg-secondary-bg !py-1 !px-0 !bg-primary-tint !border-y-border !border-x-black"
                >
                  {props.children}
                </th>
              );
            },
          },
        }}
      />
    </div>
  );
};

export default function TableArea({
  label,
  selectedDate,
  setselectedDate,
  activeProduct,
  activeTab,
  isProductListEmpty,
  subsectionList,
  setHiddenSidebar,
  hiddenSidebar,
  productsList,
}) {
  const contentRef = useRef();
  const mannufacturerRef = useRef();
  const expImpRef = useRef();
  const impRegRef = useRef();
  const impCtyMnfConRef = useRef();
  const customersRef = useRef();

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const anchorRef = useRef(null);

  const {
    data: tableData,
    isLoading,
    isFetching,
    isError,
    isSuccess,
    isUninitialized,
  } = useFetchTableDataQuery(
    {
      url:
        activeTab === GLOBAL_CONSULTATION
          ? "fetch-global-consumption-data"
          : activeTab === REGULATORY_FILLING
            ? "fetch-reg-filling-data"
            : activeTab === FDF_US
              ? "fetch-fdf-us-data"
              : activeTab === FDF_WORLDWIDE
                ? "fetch-fdf-worldwide-data"
                : ![
                      GLOBAL_CONSULTATION,
                      REGULATORY_FILLING,
                      FDF_US,
                      FDF_WORLDWIDE,
                    ].includes(activeTab)
                  ? "product-dashboard-imports-and-exports"
                  : "",
      params: {
        productID: activeProduct,
        ProductId: activeProduct,
        TypeMasterId: activeTab,
        SubTypeMasterId: activeTab,
        allProducts: false,
        month: selectedDate?.split(" ")[0].toLowerCase(),
        year: selectedDate?.split(" ")[1],
      },
    },
    {
      skip: !activeProduct || !selectedDate || !activeTab,
      refetchOnMountOrArgChange: true,
    },
  );

  const { data: calenderMonth } = useFetchCalenderMonthsQuery(
    {
      ProductId: activeProduct,
      SubTypeMasterId: activeTab,
      year: selectedDate?.split(" ")[1],
    },
    {
      skip: !activeProduct || !selectedDate?.split(" ")[1] || !activeTab,
      refetchOnMountOrArgChange: true,
    },
  );

  const [fetchHTML, { isLoading: isHTMLLoading, isFetching: isHTMLFetching }] =
    useLazyFetchHTMLQuery();

  const [downloadFile, { isLoading: downloadFileLoading }] =
    useDownloadFileMutation();

  const handleDownload = async (payload, filename) => {
    const response = await downloadFile(payload);
    if (response) {
      downloadBlobAsFile(response.data, `${filename}.pdf`);
    }
  };

  const getFormattedData = (data) => {
    if (Array.isArray(data)) {
      if (data.every((item) => Array.isArray(item))) {
        return { sheet: data };
      } else {
        return { sheet: objectsToArrays(data) };
      }
    }

    return data;
  };

  const getTables = () => {
    const formattedData =
      tableData && tableData?.success
        ? getFormattedData(tableData?.data || tableData?.cleanedData)
        : {};

    return Object.entries(formattedData)?.map(([key, data]) => {
      if (!data.length) return { columns: [], dataSource: [] };
      const transformedData = data.length > 0 ? arraysToObjects(data) : [];

      //data source
      const dataSource = transformedData.map((row, index) => ({
        key: index,
        ...row,
      }));

      //format style for fields
      const fieldsToFormatStyle = (column, text) => {
        //global consumption
        if (activeTab === GLOBAL_CONSULTATION && column === "%Change") {
          return Math.sign(parseFloat(text)) === -1;
          //fdf worldwide
        } else if (activeTab === FDF_WORLDWIDE && column === "Holder") {
          return (
            !tableData?.previousData?.length ||
            !tableData?.previousData?.some(
              (item) => item?.toLowerCase() === text?.toLowerCase(),
            )
          );
          //fdf us
        } else if (activeTab === FDF_US && column === "Applicant Holder") {
          return (
            !tableData?.previousData?.length ||
            !tableData?.previousData?.some(
              (item) => item?.toLowerCase() === text?.toLowerCase(),
            )
          );
        } else {
          return false;
        }
      };

      //get status for reg filling (CEP, WC, US DMF, CDMF)
      const getStatus = (data, record, column) => {
        const GRAY = "!text-gray-500 !opacity-50";
        const RED = "!text-red-500";
        if (
          data &&
          !data.some(
            ([holder]) =>
              holder.toLowerCase() === record["Holder"].toLowerCase(),
          )
        ) {
          return RED;
        } else {
          //compare if prevholder and prevdate to curholder and curdate
          const holderWithDate = data?.find(([holder, status, prevDate]) => {
            const currentHolder = record["Holder"]?.toLowerCase();
            const prevHolder = holder?.toLowerCase();
            let curDate = null;
            if (column === "CEP") {
              curDate = record["CEP Closure Date of last Procedure"];
            } else if (column === "WC") {
              curDate = record["WC Issue Date"];
            } else if (column === "US DMF") {
              curDate = record["DMF Submit Date"];
            } else if (column === "CDMF") {
              curDate = record["CDMF Latest Update"];
            }

            return (
              prevHolder === currentHolder &&
              prevDate &&
              dayjs(prevDate).isSame(dayjs(curDate))
            );
          });

          // If match → compare status.
          // I & I → grey
          // X & X → empty
          // I vs X → red
          if (holderWithDate) {
            const [prevHolder, prevStatus] = holderWithDate;
            if (prevStatus === "I" && record[column] === "I") {
              return GRAY;
            } else if (prevStatus === "X" && record[column] === "X") {
              return false;
            } else if (prevStatus !== record[column]) {
              return RED;
            } else {
              return false;
            }
          } else {
            // If Holder exists but Date empty → grey (I) or empty (X).
            const holderWithEmptyDate = data?.find(
              ([holder, status, prevDate]) =>
                holder.toLowerCase() === record["Holder"].toLowerCase() &&
                !prevDate,
            );

            if (holderWithEmptyDate) {
              const [prevHolder, prevStatus] = holderWithEmptyDate;
              if (record[column] === "I" && prevStatus === "I") {
                return GRAY;
              } else if (record[column] === "X" && prevStatus === "X") {
                return RED;
              }
            } else {
              // If same Holder exists with diff date → red if statuses differ, else grey/empty.
              const holderWithDiffDate = data?.find(
                ([holder, status, prevDate]) => {
                  const currentHolder = record["Holder"]?.toLowerCase();
                  const prevHolder = holder?.toLowerCase();
                  let curDate = null;
                  if (column === "CEP") {
                    curDate = record["CEP Closure Date of last Procedure"];
                  } else if (column === "WC") {
                    curDate = record["WC Issue Date"];
                  } else if (column === "US DMF") {
                    curDate = record["DMF Submit Date"];
                  } else if (column === "CDMF") {
                    curDate = record["CDMF Latest Update"];
                  }

                  return (
                    prevHolder === currentHolder &&
                    prevDate &&
                    !dayjs(prevDate).isSame(dayjs(curDate))
                  );
                },
              );

              if (holderWithDiffDate) {
                const [prevHolder, prevStatus] = holderWithDiffDate;
                if (record[column] !== prevStatus) {
                  return RED;
                } else if (record[column] === "X" && prevStatus === "X") {
                  return false;
                } else if (record[column] === "I" && prevStatus === "I") {
                  return GRAY;
                }
              } else {
                // If no previous entry at all → grey (I) or empty (X).
                if (record[column] === "X") {
                  return false;
                } else if (record[column] === "I") {
                  return GRAY;
                }
              }
            }
          }
        }
        return false;
      };

      //get status for reg filling (JDMF, KDMF)
      const getStatusForRest = (data, record, column) => {
        const GRAY = "!text-gray-500 !opacity-50";
        const RED = "!text-red-500";
        if (
          data &&
          !data.some(
            ([holder]) =>
              holder.toLowerCase() === record["Holder"].toLowerCase(),
          )
        ) {
          return RED;
        } else {
          const holder = data?.find(
            ([holder, status]) =>
              holder.toLowerCase() === record["Holder"].toLowerCase(),
          );
          if (holder) {
            const [prevHolder, prevStatus] = holder;
            if (prevStatus === "I" && record[column] === "I") {
              return GRAY;
            } else if (prevStatus === "X" && record[column] === "X") {
              return false;
            } else if (prevStatus !== record[column]) {
              return RED;
            } else {
              return false;
            }
          }
        }
        return false;
      };

      //format style for reg filling
      // const fieldsToFormatStyleForRegFilling = (record, column) => {
      //   const prevData = Object.keys(tableData?.previousData).length
      //     ? tableData?.previousData
      //     : null;

      //   if (prevData) {
      //     //cep
      //     if (
      //       column === "CEP" ||
      //       column === "Certificate Number / Year" ||
      //       column === "CEP Closure Date of last Procedure"
      //     ) {
      //       const data = prevData["Cep"];
      //       return getStatus(data, record, "CEP");
      //     } else if (column === "KDMF") {
      //       const data = prevData["KDMF"];
      //       return getStatusForRest(data, record, "KDMF");
      //     } else if (column === "WC" || column === "WC Issue Date") {
      //       const data = prevData["WC"];
      //       return getStatus(data, record, "WC");
      //     } else if (column === "US DMF" || column === "DMF Submit Date") {
      //       const data = prevData["US DMF"];
      //       return getStatus(data, record, "US DMF");
      //     } else if (column === "JDMF") {
      //       const data = prevData["JDMF"];
      //       return getStatusForRest(data, record, "JDMF");
      //     } else if (
      //       column === "CDMF" ||
      //       column === "CDMF Latest Update" ||
      //       column === "Comments"
      //     ) {
      //       const data = prevData["CDMF"];
      //       return getStatus(data, record, "CDMF");
      //     } else {
      //       return false;
      //     }
      //   } else {
      //     return "!text-red-500";
      //   }
      // };

      //get color for reg filling
      const getColor = (record, field, column) => {
        if (selectedDate === DATA_CREATION_DATE) {
          if (record[column] === "I") {
            return GRAY;
          } else {
            return BLACK;
          }
        }
        return COLORS[record[field]];
      };

      //format style for reg filling
      const fieldsToFormatStyleForRegFilling = (record, column) => {
        // cep
        if (
          column === "CEP" ||
          column === "Certificate Number / Year" ||
          column === "CEP Closure Date of last Procedure"
        ) {
          return getColor(record, CEP_COLOR, "CEP");
        } else if (column === "KDMF") {
          return getColor(record, KDMF_COLOR, "KDMF");
        } else if (column === "WC" || column === "WC Issue Date") {
          return getColor(record, WC_COLOR, "WC");
        } else if (column === "US DMF" || column === "DMF Submit Date") {
          return getColor(record, US_DMF_COLOR, "US DMF");
        } else if (column === "JDMF") {
          return getColor(record, JDMF_COLOR, "JDMF");
        } else if (
          column === "CDMF" ||
          column === "CDMF Latest Update" ||
          column === "Comments"
        ) {
          return getColor(record, CDMF_COLOR, "CDMF");
        } else {
          return false;
        }
      };

      //get filters for each columns
      const getUniqueFilters = (data, field) => {
        let values = data.map((item) => item[field]);

        if (!["CEP", "WC", "US DMF", "JDMF", "KDMF", "CDMF"].includes(field)) {
          values = values.filter(
            (value) => value !== undefined && value !== null && value !== "",
          );
        }

        return [...new Set(values)].map((value) => ({
          text: String(value),
          value,
        }));
      };

      //columns
      const columns = data[0]?.map((column, index) => ({
        title: headerCellRenderer(
          column,
          activeTab === REGULATORY_FILLING && hiddenSidebar ? "!py-2" : null,
          null,
          activeTab === REGULATORY_FILLING && hiddenSidebar ? true : false,
        ),
        dataIndex: column,
        key: column,
        hidden:
          activeTab === REGULATORY_FILLING &&
          [
            US_DMF_COLOR,
            CEP_COLOR,
            WC_COLOR,
            JDMF_COLOR,
            KDMF_COLOR,
            CDMF_COLOR,
          ].includes(column),
        fixed:
          activeTab === REGULATORY_FILLING &&
          !hiddenSidebar &&
          ["Country", "Holder"].includes(column)
            ? "left"
            : "",
        onCell: () => ({
          style:
            activeTab === REGULATORY_FILLING &&
            ["Country", "Holder"].includes(column)
              ? {
                  background: "#fff",
                  zIndex: 1,
                }
              : {},
        }),
        onHeaderCell: () => ({
          style:
            activeTab === REGULATORY_FILLING &&
            ["Country", "Holder"].includes(column)
              ? {
                  background: "#fff",
                  zIndex: 2,
                }
              : {},
        }),
        render: (text, record, recordIndex) =>
          cellRenderer(
            // value or text
            activeTab === GLOBAL_CONSULTATION &&
              !isNaN(parseFloat(text)) &&
              isFinite(text)
              ? text.startsWith("0.") || text.startsWith("-0.")
                ? text
                : Math.round(parseFloat(text))
              : text,

            // text styling
            activeTab === REGULATORY_FILLING &&
              [
                "CEP",
                "Certificate Number / Year",
                "CEP Closure Date of last Procedure",
                "WC",
                "WC Issue Date",
                "US DMF",
                "DMF Submit Date",
                "JDMF",
                "KDMF",
                "CDMF",
                "CDMF Latest Update",
                "Comments",
              ].includes(column)
              ? fieldsToFormatStyleForRegFilling(record, column)
              : fieldsToFormatStyle(column, text)
                ? `!text-red-500 ${
                    activeTab === GLOBAL_CONSULTATION && recordIndex === 0
                      ? "!bg-[#6ae0b733] !py-2.5 !px-3.5"
                      : activeTab === FDF_US || activeTab === FDF_WORLDWIDE
                        ? "!px-0"
                        : "!px-3.5"
                  }`
                : activeTab === GLOBAL_CONSULTATION
                  ? `!py-2.5 !px-3.5 ${recordIndex === 0 ? "!bg-[#6ae0b733]" : ""}`
                  : null,

            // null value
            null,
            // truncate
            activeTab === REGULATORY_FILLING && hiddenSidebar ? true : false,
          ),
        width:
          // shrinking the table for reg filling when sidebar is hidden
          activeTab === REGULATORY_FILLING && hiddenSidebar
            ? ["CEP", "WC", "US DMF", "JDMF", "KDMF", "CDMF"].includes(column)
              ? 75
              : ["Country"].includes(column)
                ? 100
                : ["Holder"].includes(column)
                  ? 300
                  : [
                        "CEP Closure Date of last Procedure",
                        "WC Issue Date",
                        "DMF Submit Date",
                        "CDMF Latest Update",
                      ].includes(column)
                    ? 100
                    : ""
            : 200,
        sorter:
          activeTab === REGULATORY_FILLING
            ? (a, b) => {
                return String(a[column])?.localeCompare(String(b[column]));
              }
            : false,
        showSorterTooltip: false,
        filters:
          activeTab === REGULATORY_FILLING
            ? getUniqueFilters(dataSource, column)
            : false,
        filterSearch: activeTab === REGULATORY_FILLING ? true : false,
        onFilter: (value, record) => record[column] === value,
      }));

      return {
        columns:
          activeTab === REGULATORY_FILLING
            ? columns.filter((col) => col.key !== "Active Ingredient")
            : columns,
        dataSource,
      };
    });
  };

  const handleDateRangeChange = (_, date) => {
    setselectedDate(date);
  };

  //set selected date to the month and year of the data
  useEffect(() => {
    if (subsectionList && activeProduct) {
      const tab = subsectionList.find((sub) => sub.id === activeTab);
      if (tab) {
        const month = tab.uploadedMonth;
        const year = tab.uploadedYear;
        if (month && year) {
          setselectedDate(`${toTitleCase(month)} ${year}`);
        }
      }
    }
  }, [activeTab, activeProduct]);

  //disable date if it is not in the calender month
  const handleDisabledDate = (current) => {
    const currentMonth = dayjs()
      .month(current.month())
      .format("MMMM")
      .toLowerCase();
    const isDateEnabled = calenderMonth?.monthsList?.some(
      (month) => String(month).toLowerCase() === currentMonth,
    );
    return !isDateEnabled;
  };

  //toggle sidebar
  const toggleSidebar = () => {
    if (hiddenSidebar) {
      setHiddenSidebar(false);
    } else {
      setHiddenSidebar(true);
    }
  };

  //download pdf getting html from BE
  const handleDownloadPDFFromBE = (tableKey = "") => {
    const updatedTableKey =
      activeTab === BRAZIL_IMPORTS
        ? "arriContOriContTbl1Json"
        : activeTab === INDIAN_IMPORTS
          ? "oriConImpTbl1Json"
          : activeTab === MEXICAN_IMPORTS
            ? "oriConExpImpTbl1Json"
            : tableKey;
    const payload = {
      url:
        activeTab === GLOBAL_CONSULTATION
          ? "get-gc-html"
          : activeTab === FDF_US
            ? "get-usapprovals-html"
            : activeTab === FDF_WORLDWIDE
              ? "get-fdfworldwide-html"
              : activeTab === REGULATORY_FILLING
                ? "get-reg.filing-html"
                : [
                      BRAZIL_IMPORTS,
                      INDIAN_IMPORTS,
                      MEXICAN_IMPORTS,
                      INDIAN_EXPORTS,
                      MEXICAN_EXPORTS,
                      BRAZIL_EXPORTS,
                    ].includes(activeTab)
                  ? "download-imports-and-exports-individual-tables"
                  : "",
      params: {
        productID: activeProduct,
        month: selectedDate?.split(" ")[0].toLowerCase(),
        year: selectedDate?.split(" ")[1],
        SubTypeMasterId: activeTab,
        tableKey: updatedTableKey,
        id: tableData?.data[0].id,
      },
    };

    if (
      [
        BRAZIL_IMPORTS,
        INDIAN_IMPORTS,
        MEXICAN_IMPORTS,
        INDIAN_EXPORTS,
        MEXICAN_EXPORTS,
        BRAZIL_EXPORTS,
      ].includes(activeTab)
    ) {
      const fileName = `${label} - ${
        productsList?.find((t) => t.id === activeProduct)?.name
      }`;

      handleDownload(payload, fileName);
    } else {
      fetchHTML(payload).then((res) => {
        if (res?.data?.success) {
          handleDownloadPDF(null, res?.data?.html);
        }
      });
    }
  };

  //download pdf
  const handleDownloadPDF = (
    ref = null,
    html = null,
    pageTitle = "",
    useDivStylingInsteadOfTable = false,
  ) => {
    const element = ref ? ref.current : contentRef.current;

    const updatedTitle = pageTitle
      ? pageTitle
      : `${label} - ${productsList?.find((t) => t.id === activeProduct)?.name}`;

    //directly downloading html coming from BE
    if (html) {
      const opt = {
        margin: 0.1,
        filename: `${updatedTitle}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: "in", format: "A4", orientation: "portrait" },
      };

      html2pdf().set(opt).from(html).save();
      return;
    }

    const wrapper = document.createElement("div");

    // Add title and ites styles
    const title = document.createElement("h1");
    title.textContent = updatedTitle;
    title.style.marginBottom = "20px";
    title.style.fontSize = "24px";
    title.style.color = "#0fa479";
    title.style.textAlign = "center";

    wrapper.appendChild(title);

    //wrapper style
    wrapper.style.display = "flex";
    wrapper.style.justifyContent = "center";
    wrapper.style.alignItems = "center";
    wrapper.style.flexDirection = "column";
    wrapper.style.gap = "1.5rem";

    // Clone and append original content
    const contentClone = element.cloneNode(true);
    // Enhanced styling for all table cells
    contentClone.querySelectorAll("td, th").forEach((cell) => {
      cell.style.padding = "10px";
      cell.style.height = "auto";
      cell.style.minHeight = "40px";
      cell.style.lineHeight = "1.4";
      cell.style.display = "table-cell";
      cell.style.boxSizing = "border-box";
      cell.style.overflow = "visible";
    });

    if (useDivStylingInsteadOfTable) {
      const container = contentClone.querySelector(".customer-list-container");
      container.style.display = "flex";
      // container.style.flexDirection = "row";
      container.style.flexWrap = "wrap";

      contentClone
        .querySelectorAll(".customer-list-container div")
        .forEach((cell) => {
          cell.style.paddingBlock = "8px";
          cell.style.textAlign = "start";
          cell.style.whiteSpace = "normal";
          cell.style.wordWrap = "wrap";
          cell.style.display = "flex";
          cell.style.flexWrap = "wrap";
          cell.style.width = "200px";
        });
    }

    wrapper.appendChild(contentClone);

    // Temporarily add to DOM for PDF generation
    document.body.appendChild(wrapper);

    const opt = {
      margin: 0.1,
      filename: `${updatedTitle}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "A4", orientation: "portrait" },
    };

    html2pdf()
      .set(opt)
      .from(wrapper)
      .save()
      .then(() => {
        // Clean up - remove the temporary wrapper
        document.body.removeChild(wrapper);
      });
  };

  const handleDownloadDocAsImageInPDF = async (
    ref = null,
    html = null,
    pageTitle = "",
    useDivStylingInsteadOfTable = false,
  ) => {
    const element = ref ? ref.current : contentRef.current;

    const updatedTitle = pageTitle
      ? pageTitle
      : `${label} - ${productsList?.find((t) => t.id === activeProduct)?.name}`;

    // If HTML string is provided directly
    if (html) {
      const wrapper = document.createElement("div");
      wrapper.innerHTML = html;
      document.body.appendChild(wrapper);

      const canvas = await html2canvas(wrapper, { scale: 2 });
      const imgData = canvas.toDataURL("image/jpeg", 1.0);
      const pdf = new jsPDF("p", "mm", "a4");

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`${updatedTitle}.pdf`);

      document.body.removeChild(wrapper);
      return;
    }

    // Create wrapper with title and cloned content
    const wrapper = document.createElement("div");

    wrapper.style.display = "flex";
    wrapper.style.justifyContent = "center";
    wrapper.style.alignItems = "center";
    wrapper.style.flexDirection = "column";
    wrapper.style.gap = "1.5rem";
    wrapper.style.width = "100%";

    const title = document.createElement("h1");
    title.textContent = updatedTitle;
    title.style.marginBottom = "20px";
    title.style.fontSize = "44px";
    title.style.color = "#0fa479";
    title.style.textAlign = "center";

    wrapper.appendChild(title);

    const contentClone = element.cloneNode(true);
    contentClone.querySelectorAll("td, th").forEach((cell) => {
      cell.style.padding = "10px";
      cell.style.height = "auto";
      cell.style.minHeight = "40px";
      cell.style.display = "table-cell";
      cell.style.justifyContent = "center";
      cell.style.alignItems = "center";
      cell.style.lineHeight = "1.4";
      cell.style.boxSizing = "border-box";
      cell.style.overflow = "visible";
    });

    if (useDivStylingInsteadOfTable) {
      const container = contentClone.querySelector(
        ".content-section .customer-list-container",
      );
      container.style.display = "flex";
      container.style.flexDirection = "row";
      container.style.flexWrap = "wrap";

      contentClone
        .querySelectorAll(".customer-list-container div")
        .forEach((cell) => {
          cell.style.paddingBlock = "8px";
          cell.style.textAlign = "start";
          cell.style.whiteSpace = "normal";
          cell.style.wordWrap = "wrap";
          cell.style.display = "flex";
          cell.style.flexWrap = "wrap";
          cell.style.width = "200px";
        });
    }

    wrapper.appendChild(contentClone);

    document.body.appendChild(wrapper);

    const canvas = await html2canvas(wrapper, { scale: 2 });
    const imgData = canvas.toDataURL("image/jpeg", 1.0);
    const pdf = new jsPDF("p", "mm", "a4");

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, pdfHeight);
    pdf.save(`${updatedTitle}.pdf`);

    document.body.removeChild(wrapper);
  };

  //download image
  const handleDownloadImageAsPDF = async (imageUrl, name) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = imageUrl;

    img.onload = () => {
      // Create canvas to compress image
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      const imgWidth = img.width;
      const imgHeight = img.height;

      // Reduce resolution for smaller file size
      const maxDimension = 1200; // Adjust this value based on quality needs
      let canvasWidth = imgWidth;
      let canvasHeight = imgHeight;

      if (imgWidth > maxDimension || imgHeight > maxDimension) {
        const ratio = Math.min(
          maxDimension / imgWidth,
          maxDimension / imgHeight,
        );
        canvasWidth = imgWidth * ratio;
        canvasHeight = imgHeight * ratio;
      }

      canvas.width = canvasWidth;
      canvas.height = canvasHeight;

      // Draw and compress image
      ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);

      // Convert to compressed JPEG (adjust quality: 0.1-1.0)
      const compressedDataUrl = canvas.toDataURL("image/jpeg", 0.7); // 70% quality

      // Create PDF with compressed image
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "pt",
        format: "a4",
        compress: true, // Enable PDF compression
      });

      const updatedTitle = name
        ? name
        : `${label} - ${
            productsList?.find((t) => t.id === activeProduct)?.name
          }`;

      // Header configuration
      const headerHeight = 60;
      const headerText = updatedTitle || "Document Title";
      const headerFontSize = 24;
      const bottomMarginRem = 1.5;
      const bottomMarginPt = bottomMarginRem * 16 * 0.75;

      // Add styled header
      pdf.setFontSize(headerFontSize);
      pdf.setFont("helvetica", "normal"); // Use standard font
      pdf.setTextColor(15, 164, 121);

      const headerTextWidth = pdf.getTextWidth(headerText);
      const headerX = (595.28 - headerTextWidth) / 2; // A4 width in pts
      pdf.text(headerText, headerX, 30);

      pdf.setTextColor(0, 0, 0);

      // Calculate available space for image
      const A4_WIDTH = 595.28;
      const A4_HEIGHT = 841.89;
      const availableHeight = A4_HEIGHT - headerHeight - bottomMarginPt;

      // Calculate aspect ratio using canvas dimensions
      const imgAspectRatio = canvasWidth / canvasHeight;
      const pageAspectRatio = A4_WIDTH / availableHeight;

      let drawWidth, drawHeight;
      if (imgAspectRatio > pageAspectRatio) {
        drawWidth = A4_WIDTH - 20; // 20pt margins
        drawHeight = drawWidth / imgAspectRatio;
      } else {
        drawHeight = availableHeight - 20;
        drawWidth = availableHeight * imgAspectRatio;
      }

      // Center the image
      const x = (A4_WIDTH - drawWidth) / 2;
      const y = headerHeight + (availableHeight - drawHeight) / 2;

      // Add compressed image to PDF
      pdf.addImage(compressedDataUrl, "JPEG", x, y, drawWidth, drawHeight);
      pdf.save(`${updatedTitle}.pdf`);
    };

    img.onerror = (err) => {
      console.error("Image failed to load", err);
    };
  };

  return (
    <div
      className={
        hiddenSidebar
          ? "w-full min-w-0 flex-1 flex flex-col gap-2.5"
          : "flex flex-col gap-2.5 w-full min-w-0 flex-1 md:w-[70%] lg:w-[80%]"
      }
    >
      {/* header */}
      <div className="flex flex-col items-start justify-between gap-2 md:items-center md:flex-row">
        <div className="flex items-center gap-2">
          {/* hide sidebar button */}
          {activeTab && hiddenSidebar ? (
            <Tooltip title="Show Products">
              <Button
                className={`font-medium hover:!border-secondary-main hover:!border hover:!text-secondary-main hover:!bg-primary-tint !px-4 w-fit !h-8 !rounded-lg text-xs border-primary-tint text-primary-tint  bg-secondary-main !leading-none`}
                variant="outlined"
                onClick={toggleSidebar}
                icon={<RightOutlined />}
              />
            </Tooltip>
          ) : null}
          <div className="flex flex-col gap-0.5">
            <label className="text-base font-semibold text-primary-tint">
              {label}{" "}
              {activeTab === GLOBAL_CONSULTATION ? "(Quarter-wise data)" : ""}
            </label>
            {[
              GLOBAL_CONSULTATION,
              FDF_US,
              FDF_WORLDWIDE,
              REGULATORY_FILLING,
            ].includes(activeTab) ? (
              <span className="text-xs font-semibold text-gray-600">
                Source:{" "}
                {activeTab === GLOBAL_CONSULTATION
                  ? "Cortellis"
                  : activeTab === FDF_US
                    ? "Orange book - Active"
                    : activeTab === REGULATORY_FILLING
                      ? "FDA, CDSCO, CDE, EDQM, Cortellis"
                      : activeTab === FDF_WORLDWIDE
                        ? "Cortellis"
                        : null}
              </span>
            ) : null}
          </div>
        </div>
        {/* action buttons */}
        <div className="flex items-center gap-2">
          {/* download pdf button */}
          {activeTab &&
          activeProduct &&
          [
            GLOBAL_CONSULTATION,
            REGULATORY_FILLING,
            FDF_US,
            FDF_WORLDWIDE,
            INDIAN_IMPORTS,
            MEXICAN_IMPORTS,
            BRAZIL_IMPORTS,
          ].includes(activeTab) ? (
            <Tooltip title="Download PDF">
              <IconButton
                loading={isHTMLFetching || isHTMLLoading || downloadFileLoading}
                icon={downloadActiveIcon}
                activeIcon={downloadIcon}
                className={`!border-primary-tint transition-all duration-300 rounded-lg cursor-pointer h-8 border hover:!bg-primary-tint px-2`}
                onClick={() =>
                  [
                    GLOBAL_CONSULTATION,
                    REGULATORY_FILLING,
                    FDF_US,
                    FDF_WORLDWIDE,
                    BRAZIL_IMPORTS,
                    INDIAN_IMPORTS,
                    MEXICAN_IMPORTS,
                  ].includes(activeTab)
                    ? handleDownloadPDFFromBE()
                    : handleDownloadPDF(contentRef)
                }
                iconClassName={"w-[18px] h-4"}
              />
            </Tooltip>
          ) : null}

          {/* calender */}
          <div ref={anchorRef} className="relative flex w-full">
            <Button
              className={`font-medium ${
                !false
                  ? "hover:!border-secondary-main hover:!border hover:!text-secondary-main hover:!bg-primary-tint"
                  : ""
              } px-4 w-fit h-8 !rounded-lg text-sm border-primary-tint text-primary-tint  bg-secondary-main !leading-none`}
              variant="outlined"
              onClick={() => setIsDatePickerOpen(true)}
              icon={<CalendarOutlined />}
            >
              {selectedDate}
            </Button>
            <DatePicker
              picker="month"
              open={isDatePickerOpen}
              format="MMMM YYYY"
              placement="bottomRight"
              value={dayjs(selectedDate, "MMMM YYYY")}
              getPopupContainer={() => anchorRef.current}
              onOpenChange={(status) => setIsDatePickerOpen(status)}
              onChange={handleDateRangeChange}
              className="!h-0 !pointer-events-none !opacity-0 !top-4 absolute"
              disabledDate={handleDisabledDate}
              onPanelChange={(value, mode) => {
                setselectedDate(
                  `${dayjs()
                    .month(value.month())
                    .format("MMMM")} ${value.year()}`,
                );
              }}
            />
          </div>
        </div>
      </div>

      {/* table */}
      {!activeProduct ? (
        <div className="flex items-center justify-center h-full">
          <Empty
            description="Select a Product"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      ) : !activeTab ? (
        <div className="flex items-center justify-center h-full">
          <Empty
            description="Select a Sub-section"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      ) : isLoading || isFetching ? (
        <div className="flex items-center justify-center h-64">
          <Spin
            indicator={
              <Loading3QuartersOutlined spin className="!text-primary-tint" />
            }
          />
        </div>
      ) : isSuccess &&
        (!tableData ||
          isProductListEmpty ||
          isError ||
          tableData?.data?.length === 0 ||
          tableData?.cleanedData?.length === 0 ||
          isUninitialized) ? (
        <div className="flex items-center justify-center h-64">
          <Empty
            description="No data available"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      ) : ![
          GLOBAL_CONSULTATION,
          REGULATORY_FILLING,
          FDF_US,
          FDF_WORLDWIDE,
        ].includes(activeTab) ? (
        <div className="flex flex-col min-w-0 gap-8 overflow-x-hidden overflow-y-auto custom-scrollbar">
          {/* indian imports */}
          {activeTab === INDIAN_IMPORTS ? (
            <>
              {/* table 1 */}
              {tableData?.data[0]?.oriConImpTbl1 ? (
                <TableWithCustomHeader
                  tableData={tableData?.data[0]?.oriConImpTbl1Json}
                  tableType="oriConImpTbl1"
                  activeTab={activeTab}
                  hiddenSidebar={hiddenSidebar}
                />
              ) : (
                // <div
                //   dangerouslySetInnerHTML={{
                //     __html: tableData?.data[0]?.oriConImpTbl1,
                //   }}
                //   ref={contentRef}
                // />
                <div className="flex items-center justify-center h-80">
                  <Empty
                    description="No data available"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                </div>
              )}
            </>
          ) : // mexican imports
          activeTab === MEXICAN_IMPORTS ? (
            <>
              {/* table 1 */}
              {tableData?.data[0]?.oriConExpImpTbl1 ? (
                <TableWithCustomHeader
                  tableData={tableData?.data[0]?.oriConExpImpTbl1Json}
                  tableType="oriConExpImpTbl1"
                  activeTab={activeTab}
                  hiddenSidebar={hiddenSidebar}
                />
              ) : (
                // <div
                //   dangerouslySetInnerHTML={{
                //     __html: tableData?.data[0]?.oriConExpImpTbl1,
                //   }}
                //   ref={contentRef}
                // />
                <div className="flex items-center justify-center h-80">
                  <Empty
                    description="No data available"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                </div>
              )}
            </>
          ) : // brazil imports
          activeTab === BRAZIL_IMPORTS ? (
            <>
              {/* table 1 */}
              {tableData?.data[0]?.arriContOriContTbl1 ? (
                <TableWithCustomHeader
                  tableData={tableData?.data[0]?.arriContOriContTbl1Json}
                  tableType="arriContOriContTbl1"
                  activeTab={activeTab}
                  hiddenSidebar={hiddenSidebar}
                />
              ) : (
                // <div
                //   dangerouslySetInnerHTML={{
                //     __html: tableData?.data[0]?.arriContOriContTbl1,
                //   }}
                //   ref={contentRef}
                // />
                <div className="flex items-center justify-center h-80">
                  <Empty
                    description="No data available"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                </div>
              )}
            </>
          ) : (
            // all exports
            <>
              {/* charts */}
              <div
                ref={contentRef}
                className="grid grid-cols-1 gap-8 lg:grid-cols-2"
              >
                {/* qunatity chart  */}
                <div className="flex flex-col-reverse items-end gap-2">
                  <img
                    src={tableData?.data[0]?.qtyChart}
                    alt="quantity chart"
                    className="object-cover w-full h-full"
                  />
                  <Tooltip title="Download Quantity Chart">
                    <IconButton
                      icon={downloadActiveIcon}
                      activeIcon={downloadIcon}
                      className={`!border-primary-tint transition-all duration-300 rounded-lg cursor-pointer h-8 border hover:!bg-primary-tint px-2`}
                      onClick={() =>
                        handleDownloadImageAsPDF(tableData?.data[0]?.qtyChart)
                      }
                      iconClassName={"w-3 h-3"}
                    />
                  </Tooltip>
                </div>

                {/* usd chart  */}
                <div className="flex flex-col-reverse items-end gap-2">
                  <img
                    src={tableData?.data[0]?.usdChart}
                    alt="usd chart"
                    className="object-cover w-full h-full"
                  />

                  <Tooltip title="Download USD Chart">
                    <IconButton
                      icon={downloadActiveIcon}
                      activeIcon={downloadIcon}
                      className={`!border-primary-tint transition-all duration-300 rounded-lg cursor-pointer h-8 border hover:!bg-primary-tint px-2`}
                      onClick={() =>
                        handleDownloadImageAsPDF(tableData?.data[0]?.usdChart)
                      }
                      iconClassName={"w-3 h-3"}
                    />
                  </Tooltip>
                </div>
              </div>

              {/* table 1 */}
              <div className="grid min-w-0 grid-cols-[1fr_30px] gap-2">
                <div className="min-w-0">
                  <TableWithCustomHeader
                    tableData={tableData?.data[0]?.manufTbl1Json}
                    tableType="manufTbl1"
                    activeTab={activeTab}
                    hiddenSidebar={hiddenSidebar}
                  />
                </div>
                {/* <div
                    dangerouslySetInnerHTML={{
                      __html: tableData?.data[0]?.manufTbl1,
                    }}
                    ref={mannufacturerRef}
                  /> */}

                <Tooltip title="Download">
                  <IconButton
                    icon={downloadActiveIcon}
                    activeIcon={downloadIcon}
                    className={`!border-primary-tint transition-all duration-300 rounded-lg cursor-pointer h-8 border hover:!bg-primary-tint px-2`}
                    onClick={() => handleDownloadPDFFromBE("manufTbl1Json")}
                    iconClassName={"w-3 h-3"}
                    loading={downloadFileLoading}
                  />
                </Tooltip>
              </div>

              {/* table 2 */}
              <div className="grid min-w-0 grid-cols-[1fr_30px] gap-2">
                <div className="min-w-0">
                  <TableWithCustomHeader
                    tableData={tableData?.data[0]?.expImpTbl4Json}
                    tableType="expImpTbl4"
                    activeTab={activeTab}
                    hiddenSidebar={hiddenSidebar}
                  />
                </div>
                {/* <div
                  dangerouslySetInnerHTML={{
                    __html: tableData?.data[0]?.expImpTbl4,
                  }}
                  ref={expImpRef}
                /> */}

                <Tooltip title="Download">
                  <IconButton
                    icon={downloadActiveIcon}
                    activeIcon={downloadIcon}
                    className={`!border-primary-tint transition-all duration-300 rounded-lg cursor-pointer h-8 border hover:!bg-primary-tint px-2`}
                    onClick={() => handleDownloadPDFFromBE("expImpTbl4Json")}
                    iconClassName={"w-3 h-3"}
                    loading={downloadFileLoading}
                  />
                </Tooltip>
              </div>

              {/* table 3 */}
              <div className="grid min-w-0 grid-cols-[1fr_30px] gap-2">
                <div className="min-w-0">
                  <TableWithCustomHeader
                    tableData={tableData?.data[0]?.impRegTbl2Json}
                    tableType="impRegTbl2"
                    activeTab={activeTab}
                    hiddenSidebar={hiddenSidebar}
                  />
                </div>
                {/* <div
                  dangerouslySetInnerHTML={{
                    __html: tableData?.data[0]?.impRegTbl2,
                  }}
                  ref={impRegRef}
                /> */}
                <Tooltip title="Download">
                  <IconButton
                    icon={downloadActiveIcon}
                    activeIcon={downloadIcon}
                    className={`!border-primary-tint transition-all duration-300 rounded-lg cursor-pointer h-8 border hover:!bg-primary-tint px-2`}
                    onClick={() => handleDownloadPDFFromBE("impRegTbl2Json")}
                    iconClassName={"w-3 h-3"}
                    loading={downloadFileLoading}
                  />
                </Tooltip>
              </div>

              {/* table 4 */}
              <div className="grid min-w-0 grid-cols-[1fr_30px] gap-2">
                <div className="flex flex-col min-w-0 gap-4">
                  {tableData?.data[0]?.impConManConsTbl3Json?.map(
                    (table, index) => {
                      const tableName =
                        tableData?.data[0]?.impRegTbl2Json?.dataRowsHtml[
                          index
                        ][0];
                      return (
                        <div className="flex flex-col gap-1">
                          <label className="text-sm font-semibold text-primary-tint">
                            {tableName}
                          </label>
                          <TableWithCustomHeader
                            tableData={table}
                            tableType="impConManConsTbl3"
                            activeTab={activeTab}
                            hiddenSidebar={hiddenSidebar}
                          />
                        </div>
                      );
                    },
                  )}
                </div>
                {/* <div
                  dangerouslySetInnerHTML={{
                    __html: tableData?.data[0]?.impConManConsTbl3,
                  }}
                  ref={impCtyMnfConRef}
                /> */}
                <Tooltip title="Download">
                  <IconButton
                    icon={downloadActiveIcon}
                    activeIcon={downloadIcon}
                    className={`!border-primary-tint transition-all duration-300 rounded-lg cursor-pointer h-8 border hover:!bg-primary-tint px-2`}
                    onClick={() =>
                      handleDownloadPDFFromBE("impConManConsTbl3Json")
                    }
                    iconClassName={"w-3 h-3"}
                    loading={downloadFileLoading}
                  />
                </Tooltip>
              </div>

              {/* table 5 */}
              <div className="flex items-start gap-2">
                <div
                  dangerouslySetInnerHTML={{
                    __html: tableData?.data[0]?.customerList,
                  }}
                  ref={customersRef}
                />
                {/* <Tooltip title="Download">
                  <IconButton
                    icon={downloadActiveIcon}
                    activeIcon={downloadIcon}
                    className={`!border-primary-tint transition-all duration-300 rounded-lg cursor-pointer h-8 border hover:!bg-primary-tint px-2`}
                    onClick={() =>
                      handleDownloadDocAsImageInPDF(
                        customersRef,
                        null,
                        null,
                        true
                      )
                    }
                    iconClassName={"w-3 h-3"}
                  />
                </Tooltip> */}
              </div>
            </>
          )}
        </div>
      ) : (
        <div
          ref={contentRef}
          className={`flex flex-col gap-4 [&_.ant-table-column-sorter-up]:!text-white [&_.ant-table-column-sorter-down]:!text-white  [&_.ant-table-column-sorter-up.active]:!text-gray-500
  [&_.ant-table-column-sorter-down.active]:!text-gray-500  [&_.ant-table-filter-trigger]:text-white`}
        >
          {getTables().map((table) => (
            <Table
              key={table.key}
              columns={table.columns}
              dataSource={table.dataSource}
              bodyCellClassName={
                activeTab === GLOBAL_CONSULTATION
                  ? "!border-border !py-0 !px-0"
                  : null
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}
